// Booking-source helpers — shared by Manifest, Calendar and Schedule.
// Purely a FRONT-END show/hide concern: classifying where a booking came from
// so users can toggle visibility per channel. Never touches the integration
// connections themselves (those live in Marketing → Integrations).
import { useCallback, useState } from 'react';

export const SOURCE_DEFS = [
  { key: 'bookingflows', label: 'BookingFlows', logo: '/logo-icon.png' },
  { key: 'getmyboat', label: 'GetMyBoat', logo: '/images/integrations/getmyboat-logo.png' },
  { key: 'getyourguide', label: 'GetYourGuide', logo: '/images/integrations/gyg-icon.jpg' },
];

export const ALL_SOURCE_KEYS = SOURCE_DEFS.map((s) => s.key);

// Classify a booking row (handles both camelCase and snake_case field names).
export const bookingSourceKey = (b) => {
  const s = `${b?.source || ''} ${b?.bookingSource || b?.booking_source || ''}`.toLowerCase();
  if (s.includes('getmyboat') || s.includes('gmb')) return 'getmyboat';
  if (s.includes('getyourguide') || s.includes('gyg')) return 'getyourguide';
  return 'bookingflows';
};

const STORAGE_KEY = 'bf_source_filter_v1';

const readStored = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (Array.isArray(raw) && raw.length > 0 && raw.every((k) => ALL_SOURCE_KEYS.includes(k))) {
      return raw;
    }
  } catch { /* corrupted → defaults */ }
  return [...ALL_SOURCE_KEYS];
};

// Hook: [enabledKeys, toggle(key), filterFn, allOn]
export const useSourceFilter = () => {
  const [enabled, setEnabled] = useState(readStored);

  const toggle = useCallback((key) => {
    setEnabled((prev) => {
      let next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      if (next.length === 0) next = [key]; // never allow an empty view — keep at least one on
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  }, []);

  const filterFn = useCallback(
    (booking) => enabled.includes(bookingSourceKey(booking)),
    [enabled]
  );

  const resetAll = useCallback(() => {
    setEnabled([...ALL_SOURCE_KEYS]);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(ALL_SOURCE_KEYS)); } catch { /* private mode */ }
  }, []);

  return [enabled, toggle, filterFn, enabled.length === ALL_SOURCE_KEYS.length, resetAll];
};
